import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Plus, Eye, Edit, Copy, Trash2, FileDown, Search, FileText, LayoutGrid, List, Check, X, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Trash, User, Loader2, FileSpreadsheet } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getPhotoUrl } from '@/utils/photoUrl';
import { SortableTh } from '@/components/ui/SortableTh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Borrador: 'secondary',
  Enviada: 'default',
  Adjudicada: 'outline',
};

const isVencida = (vehiculo) => {
  if (vehiculo.estadoPublicacion === 'En espera' && vehiculo.fechaEspera) {
    return new Date(vehiculo.fechaEspera) < new Date();
  }
  if (!vehiculo.fechaLimiteOfertas) return false;
  if (['Adjudicada', 'Borrador', 'En espera', 'No adjudicada'].includes(vehiculo.estadoPublicacion)) return false;
  return new Date(vehiculo.fechaLimiteOfertas) < new Date();
};

const getBadgeVencidaText = (vehiculo) => {
  if (vehiculo.estadoPublicacion === 'En espera' && vehiculo.fechaEspera && new Date(vehiculo.fechaEspera) < new Date()) {
    return 'En espera vencida';
  }
  return 'Vencida';
};

const Publicaciones = ({ isMisPublicaciones = false }) => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'Admin' || user?.rol === 'SuperAdmin';
  const isSuperAdmin = user?.rol === 'SuperAdmin' || user?.isSuperAdmin;
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  
  const [usuarios, setUsuarios] = useState([]);
  const [changeAuthorDialog, setChangeAuthorDialog] = useState(null);
  const [selectedNewAuthor, setSelectedNewAuthor] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const serverChunkSize = 100;

  const [searchParams] = useSearchParams();

  const parseEstadoParam = (params) => {
    const rawAll = params.getAll('estado');
    const estados = [];
    for (const item of rawAll) {
      if (item) {
        estados.push(...item.split(',').map(s => s.trim().toLowerCase()));
      }
    }
    if (estados.includes('atencion') || estados.includes('requiere_atencion')) {
      return 'atencion';
    }
    if (estados.includes('vencida') && (estados.includes('en_espera') || estados.includes('espera') || estados.includes('en espera'))) {
      return 'atencion';
    }
    if (estados.includes('vencida')) {
      return 'Vencida';
    }
    if (estados.includes('en_espera') || estados.includes('espera') || estados.includes('en espera')) {
      return 'En espera';
    }
    if (estados.includes('borrador')) return 'Borrador';
    if (estados.includes('enviada')) return 'Enviada';
    if (estados.includes('adjudicada')) return 'Adjudicada';
    if (estados.includes('no_adjudicada') || estados.includes('no adjudicada')) return 'No adjudicada';
    if (estados.includes('papelera')) return 'papelera';
    return 'all';
  };

  const initialEstado = parseEstadoParam(searchParams);

  // Filtros
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState(initialEstado);

  useEffect(() => {
    const parsed = parseEstadoParam(searchParams);
    if (parsed !== 'all') {
      setEstadoFilter(parsed);
    }
  }, [searchParams]);
  const [viewMode, setViewMode] = useState('list'); // 'cards' o 'list' - default: list
  const [selectedVehiculoOfertas, setSelectedVehiculoOfertas] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [revendedores, setRevendedores] = useState({});
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [permanentDeleteId, setPermanentDeleteId] = useState(null);
  const [processingOffer, setProcessingOffer] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Load dictionaries on mount
  useEffect(() => {
    loadDictionaries();
  }, []);

  // Debounce search input
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  // Reload chunk 0 when filters change
  useEffect(() => {
    setCurrentPage(1);
    loadVehiculosChunk(0);
  }, [searchTerm, estadoFilter, sortField, sortDirection, isMisPublicaciones]);

  // Automatically fetch next chunk if user paginates beyond local cache bounds
  useEffect(() => {
    const requiredStartIndex = (currentPage - 1) * pageSize;
    if (requiredStartIndex >= vehiculos.length && vehiculos.length < totalCount) {
      loadVehiculosChunk(vehiculos.length);
    }
  }, [currentPage]);

  const loadDictionaries = async () => {
    try {
      const promises = [
        axios.get(`${API}/revendedores?includeDeleted=true`)
      ];
      if (isSuperAdmin) {
        promises.push(axios.get(`${API}/usuarios`));
      }
      const results = await Promise.all(promises);
      
      const revMap = {};
      results[0].data.forEach(r => { revMap[r.id] = r; });
      setRevendedores(revMap);
      
      if (isSuperAdmin && results.length > 1) {
        setUsuarios(results[1].data);
      }
    } catch (e) {
      console.error('Error dictionaries:', e);
    }
  };

  const loadVehiculosChunk = async (skip) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/vehiculos/paginated`, {
        params: {
          skip,
          limit: serverChunkSize,
          searchTerm: searchTerm || undefined,
          estadoFilter: estadoFilter !== 'all' ? estadoFilter : undefined,
          sortField,
          sortDirection,
          misPublicaciones: isMisPublicaciones
        }
      });

      const { items, total } = res.data;
      setTotalCount(total);

      if (skip === 0) {
        setVehiculos(items);
      } else {
        setVehiculos(prev => {
          // avoid duplicates if React Strict Mode double-fires
          const newIds = items.map(i => i.id);
          const filteredPrev = prev.filter(p => !newIds.includes(p.id));
          return [...filteredPrev, ...items];
        });
      }
    } catch (error) {
      console.error('Error loading vehiculos chunk:', error);
      toast.error('Error al cargar publicaciones');
    } finally {
      setLoading(false);
    }
  };

  const refreshFullState = () => {
    loadDictionaries();
    loadVehiculosChunk(0);
  };

  const handleViewOfertas = async (vehiculo) => {
    setSelectedVehiculoOfertas(vehiculo);
    try {
      const res = await axios.get(`${API}/ofertas/vehiculo/${vehiculo.id}`);
      setOfertas(res.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Error al cargar ofertas del vehículo');
    }
  };

  const handleAcceptOffer = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      refreshFullState();
      const updatedVehiculo = await axios.get(`${API}/vehiculos/${selectedVehiculoOfertas.id}`);
      setSelectedVehiculoOfertas(updatedVehiculo.data);
      const res = await axios.get(`${API}/ofertas/vehiculo/${selectedVehiculoOfertas.id}`);
      setOfertas(res.data);
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleRejectOffer = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      refreshFullState();
      const res = await axios.get(`${API}/ofertas/vehiculo/${selectedVehiculoOfertas.id}`);
      setOfertas(res.data);
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const sortConfig = sortField ? { key: sortField, direction: sortDirection } : null;

  const requestSort = (field) => {
    if (sortConfig && sortConfig.key === field && sortConfig.direction === 'asc') {
      setSortDirection('desc');
    } else if (sortConfig && sortConfig.key === field && sortConfig.direction === 'desc') {
      setSortField('createdAt');
      setSortDirection('desc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/vehiculos/${id}`);
      toast.success('Publicación movida a papelera');
      refreshFullState();
    } catch (error) {
      console.error('Error deleting vehiculo:', error);
      toast.error('Error al eliminar publicación');
    }
    setDeleteId(null);
  };

  const handleRestore = async (id) => {
    try {
      await axios.patch(`${API}/vehiculos/${id}/restore`);
      toast.success('Publicación restaurada');
      refreshFullState();
    } catch (error) {
      console.error('Error restoring vehiculo:', error);
      toast.error('Error al restaurar publicación');
    }
  };

  const handlePermanentDelete = async (id) => {
    try {
      await axios.delete(`${API}/vehiculos/${id}/permanent`);
      toast.success('Publicación eliminada definitivamente');
      refreshFullState();
    } catch (error) {
      console.error('Error permanently deleting vehiculo:', error);
      toast.error('Error al eliminar definitivamente');
    }
    setPermanentDeleteId(null);
  };

  // Pagination bounds to display
  const currentDisplayVehiculos = vehiculos.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleChangeAuthor = async () => {
    try {
      if (!selectedNewAuthor || !changeAuthorDialog) return;
      const newAuthorUser = usuarios.find(u => u.id === selectedNewAuthor);
      if (!newAuthorUser) return;
      
      await axios.put(`${API}/vehiculos/${changeAuthorDialog}`, {
        autor: newAuthorUser.nombre,
        autorId: newAuthorUser.id
      });
      toast.success("Autor cambiado con éxito");
      setChangeAuthorDialog(null);
      setSelectedNewAuthor('');
      refreshFullState();
    } catch (error) {
      console.error('Error cambiando autor:', error);
      toast.error('Error al cambiar de autor');
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      toast.info('Generando archivo PDF desde el servidor...');
      const response = await axios.get(`${API}/vehiculos/export/pdf`, {
        params: {
          searchTerm: searchTerm || undefined,
          estadoFilter: estadoFilter !== 'all' ? estadoFilter : undefined,
          sortField,
          sortDirection,
          misPublicaciones: isMisPublicaciones
        },
        responseType: 'blob'
      });

      let filename = `publicaciones_${new Date().toISOString().slice(0, 10)}.pdf`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        const matches = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Archivo PDF descargado con éxito');
    } catch (error) {
      console.error('Error exportando PDF:', error);
      toast.error('Error al generar la exportación a PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      toast.info('Generando archivo Excel desde el servidor...');
      const response = await axios.get(`${API}/vehiculos/export/excel`, {
        params: {
          searchTerm: searchTerm || undefined,
          estadoFilter: estadoFilter !== 'all' ? estadoFilter : undefined,
          sortField,
          sortDirection,
          misPublicaciones: isMisPublicaciones
        },
        responseType: 'blob'
      });

      let filename = `publicaciones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        const matches = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Archivo Excel descargado con éxito');
    } catch (error) {
      console.error('Error exportando Excel:', error);
      toast.error('Error al generar la exportación a Excel');
    } finally {
      setExportingExcel(false);
    }
  };



  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">
                {isMisPublicaciones ? 'Mis Publicaciones' : 'Publicaciones'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {totalCount} publicaciones
              </p>
            </div>
            <div className="flex gap-2">
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-r-none"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button asChild data-testid="new-listing-button">
                <Link to="/publicaciones/nueva">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Publicación
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por ID, marca, modelo o autor..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                  }
                }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid={isMisPublicaciones ? "estado-filter-mis" : "estado-filter"}>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="atencion">⚠️ Requiere Atención</SelectItem>
                <SelectItem value="Borrador">Borrador</SelectItem>
                <SelectItem value="Enviada">Enviada</SelectItem>
                <SelectItem value="Vencida">Vencida</SelectItem>
                <SelectItem value="Adjudicada">Adjudicada</SelectItem>
                <SelectItem value="En espera">En espera</SelectItem>
                <SelectItem value="No adjudicada">No adjudicada</SelectItem>
                <SelectItem value="papelera">🗑️ Papelera</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={exportingPDF || exportingExcel}
                data-testid="export-pdf-button"
              >
                {exportingPDF ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={exportingExcel || exportingPDF}
                data-testid="export-excel-button"
              >
                {exportingExcel ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                )}
                Excel
              </Button>
            </div>
          </div>
        </Card>

        {/* Lista de publicaciones */}
        {loading && vehiculos.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="text-muted-foreground">Cargando publicaciones...</div>
            </div>
          </Card>
        ) : estadoFilter === 'papelera' ? (
          totalCount === 0 ? (
            <Card className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <Trash className="h-12 w-12 text-muted-foreground" />
                <div>
                  <h3 className="text-lg font-semibold mb-1">La papelera está vacía</h3>
                  <p className="text-muted-foreground">Las publicaciones eliminadas aparecerán aquí</p>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <SortableTh label="Vehículo" sortKey="vehiculo" sortConfig={sortConfig} requestSort={requestSort} />
                      <SortableTh label="Estado anterior" sortKey="estadoPublicacion" sortConfig={sortConfig} requestSort={requestSort} />
                      <SortableTh label="Eliminado" sortKey="deletedAt" sortConfig={sortConfig} requestSort={requestSort} />
                      <th className="text-right p-4 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentDisplayVehiculos.map((vehiculo) => (
                      <tr key={vehiculo.id} className="border-b last:border-0 hover:bg-accent/50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                              {vehiculo.fotos && vehiculo.fotos[0] ? (
                                <img src={getPhotoUrl(vehiculo.fotos[0])} alt={vehiculo.marca} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FileText className="h-6 w-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium">
                                {isSuperAdmin ? (
                                  <Link to={`/publicaciones/${vehiculo.id}`} className="hover:underline text-primary cursor-pointer">
                                    {vehiculo.marca} {vehiculo.modelo}
                                  </Link>
                                ) : (
                                  `${vehiculo.marca} ${vehiculo.modelo}`
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">{vehiculo.version} • {vehiculo.anio}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          {isVencida(vehiculo) ? (
                            <Badge variant="destructive">
                              {getBadgeVencidaText(vehiculo)}
                            </Badge>
                          ) : (
                            <Badge variant={badgeVariants[vehiculo.estadoPublicacion]}>
                              {vehiculo.estadoPublicacion}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(vehiculo.deletedAt).toLocaleDateString('es-UY', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2 items-center">
                            {isSuperAdmin && (
                              <Button variant="ghost" size="sm" asChild title="Ver detalle">
                                <Link to={`/publicaciones/${vehiculo.id}`} className="gap-1.5 text-xs">
                                  <Eye className="h-4 w-4" />
                                  Ver detalle
                                </Link>
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleRestore(vehiculo.id)} className="gap-2">
                              <RotateCcw className="h-4 w-4" />
                              Restaurar
                            </Button>
                            {isSuperAdmin && (
                              <Button variant="outline" size="sm" onClick={() => setPermanentDeleteId(vehiculo.id)} className="gap-2 text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                                Eliminar definitivamente
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )
        ) : totalCount === 0 ? (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <div>
                <h3 className="text-lg font-semibold mb-1">
                  {vehiculos.length === 0 ? 'No hay publicaciones' : 'No se encontraron resultados'}
                </h3>
                <p className="text-muted-foreground">
                  {vehiculos.length === 0
                    ? 'Comienza creando tu primera publicación'
                    : 'Intenta con otros filtros de búsqueda'}
                </p>
              </div>
              {vehiculos.length === 0 && (
                <Button asChild>
                  <Link to="/publicaciones/nueva">
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Primera Publicación
                  </Link>
                </Button>
              )}
            </div>
          </Card>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-4">
            {currentDisplayVehiculos.map((vehiculo) => (
              <Card key={vehiculo.id} className="p-4 hover:shadow-md transition-shadow" data-testid="vehiculo-card">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Imagen */}
                  <div className="w-full sm:w-32 h-32 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    {vehiculo.fotos && vehiculo.fotos[0] ? (
                      <img
                        src={getPhotoUrl(vehiculo.fotos[0])}
                        alt={`${vehiculo.marca} ${vehiculo.modelo}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Información */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">
                          {vehiculo.marca} {vehiculo.modelo}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {vehiculo.version} • {vehiculo.anio} • {vehiculo.km.toLocaleString()} km
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Autor: {vehiculo.autor}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Fin Ofertas: {vehiculo.fechaLimiteOfertas ? new Date(vehiculo.fechaLimiteOfertas).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {isVencida(vehiculo) ? (
                          <Badge variant="destructive" data-testid="listing-status-badge">
                            {getBadgeVencidaText(vehiculo)}
                          </Badge>
                        ) : (
                          <Badge variant={badgeVariants[vehiculo.estadoPublicacion]} data-testid="listing-status-badge">
                            {vehiculo.estadoPublicacion}
                          </Badge>
                        )}
                        {vehiculo.estadoPublicacion === 'Adjudicada' && vehiculo.fechaAdjudicacion && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(vehiculo.fechaAdjudicacion).toLocaleDateString('es-UY')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-lg font-bold text-primary">
                        {vehiculo.precioBase ? `$${vehiculo.precioBase.toLocaleString('en-US')}` : 'Sin Base'}
                      </span>
                      {vehiculo.precioAdjudicado && (
                        <span className="text-sm text-muted-foreground">
                          (Adjudicado: ${vehiculo.precioAdjudicado.toLocaleString('en-US')})
                        </span>
                      )}
                    </div>
                    {vehiculo.estadoPublicacion === 'No adjudicada' && vehiculo.comentarioNoAdjudicada && (
                      <div className="mt-2 text-sm text-destructive bg-destructive/10 p-2 rounded-md">
                        <span className="font-semibold">Motivo:</span> {vehiculo.comentarioNoAdjudicada}
                      </div>
                    )}
                    {vehiculo.estadoPublicacion === 'En espera' && vehiculo.motivoEspera && (
                      <div className="mt-2 text-sm text-amber-600 bg-amber-500/10 p-2 rounded-md dark:text-amber-400">
                        <span className="font-semibold">Motivo:</span> {vehiculo.motivoEspera}
                      </div>
                    )}

                    {/* Acciones */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleViewOfertas(vehiculo)}
                        className="gap-2"
                        data-testid="view-offers-card-button"
                      >
                        <TrendingUp className="h-4 w-4" />
                        Ofertas ({vehiculo.ofertasCount || 0})
                      </Button>
                      <Button asChild variant="outline" size="sm" data-testid="view-detail-button">
                        <Link to={`/publicaciones/${vehiculo.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          Ver Detalle
                        </Link>
                      </Button>
                      {vehiculo.estadoPublicacion === 'Borrador' && (
                        <Button asChild variant="outline" size="sm">
                          <Link to={`/publicaciones/nueva?edit=${vehiculo.id}`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </Link>
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button variant="outline" size="sm" onClick={() => setChangeAuthorDialog(vehiculo.id)}>
                          <User className="h-4 w-4 mr-2" />
                          Autor
                        </Button>
                      )}
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/publicaciones/nueva?duplicar=${vehiculo.id}`}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicar
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteId(vehiculo.id)}
                        data-testid="delete-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="border-b">
                  <tr>
                    <SortableTh label="Vehículo" sortKey="vehiculo" sortConfig={sortConfig} requestSort={requestSort} />
                    <SortableTh label="Año" sortKey="anio" sortConfig={sortConfig} requestSort={requestSort} />
                    <SortableTh label="Km" sortKey="km" sortConfig={sortConfig} requestSort={requestSort} />
                    <SortableTh label="Autor" sortKey="autor" sortConfig={sortConfig} requestSort={requestSort} />
                    <SortableTh label="Precio" sortKey="precioBase" sortConfig={sortConfig} requestSort={requestSort} />
                    <th className="text-left p-4 font-medium">Ofertas</th>
                    <SortableTh label="Estado" sortKey="estadoPublicacion" sortConfig={sortConfig} requestSort={requestSort} />
                    <SortableTh label="Fin Ofertas" sortKey="fechaLimiteOfertas" sortConfig={sortConfig} requestSort={requestSort} />
                    <SortableTh label="Fecha Pub." sortKey="createdAt" sortConfig={sortConfig} requestSort={requestSort} />
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDisplayVehiculos.map((vehiculo) => (
                    <tr key={vehiculo.id} className="border-b last:border-0 hover:bg-accent/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                            {vehiculo.fotos && vehiculo.fotos[0] ? (
                              <img src={getPhotoUrl(vehiculo.fotos[0])} alt={vehiculo.marca} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FileText className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{vehiculo.marca} {vehiculo.modelo}</div>
                            <div className="text-sm text-muted-foreground">{vehiculo.version}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">{vehiculo.anio}</td>
                      <td className="p-4">{vehiculo.km.toLocaleString()}</td>
                      <td className="p-4 text-sm">{vehiculo.autor}</td>
                      <td className="p-4 font-medium">{vehiculo.precioBase ? `$${vehiculo.precioBase.toLocaleString('en-US')}` : 'Sin Base'}</td>
                      <td className="p-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOfertas(vehiculo)}
                          className="gap-2"
                          data-testid="view-offers-button"
                        >
                          <TrendingUp className="h-4 w-4" />
                          {vehiculo.ofertasCount || 0}
                        </Button>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          {isVencida(vehiculo) ? (
                            <Badge variant="destructive" className="w-fit">
                              {getBadgeVencidaText(vehiculo)}
                            </Badge>
                          ) : (
                            <Badge variant={badgeVariants[vehiculo.estadoPublicacion]} className="w-fit">
                              {vehiculo.estadoPublicacion}
                            </Badge>
                          )}
                          {vehiculo.estadoPublicacion === 'Adjudicada' && vehiculo.fechaAdjudicacion && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(vehiculo.fechaAdjudicacion).toLocaleDateString('es-UY')}
                            </span>
                          )}
                          {vehiculo.estadoPublicacion === 'No adjudicada' && vehiculo.comentarioNoAdjudicada && (
                            <span className="text-xs text-destructive max-w-[200px] truncate" title={vehiculo.comentarioNoAdjudicada}>
                              {vehiculo.comentarioNoAdjudicada}
                            </span>
                          )}
                          {vehiculo.estadoPublicacion === 'En espera' && vehiculo.motivoEspera && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 max-w-[200px] truncate" title={vehiculo.motivoEspera}>
                              {vehiculo.motivoEspera}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                        {vehiculo.fechaLimiteOfertas ? (
                          <div className="flex flex-col">
                            <span>{new Date(vehiculo.fechaLimiteOfertas).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                            <span className="text-xs opacity-75">{new Date(vehiculo.fechaLimiteOfertas).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })} hs</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(vehiculo.createdAt).toLocaleDateString('es-UY', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button asChild variant="default" size="sm">
                            <Link to={`/publicaciones/${vehiculo.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {vehiculo.estadoPublicacion === 'Borrador' && (
                            <Button asChild variant="outline" size="sm">
                              <Link to={`/publicaciones/nueva?edit=${vehiculo.id}`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <Button variant="outline" size="sm" onClick={() => setChangeAuthorDialog(vehiculo.id)} title="Cambiar Autor">
                              <User className="h-4 w-4" />
                            </Button>
                          )}
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/publicaciones/nueva?duplicar=${vehiculo.id}`}>
                              <Copy className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteId(vehiculo.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / pageSize)}
          onPageChange={setCurrentPage}
          className="pb-8"
        />
      </div>

      {/* Modal de Ofertas */}
      <Dialog open={!!selectedVehiculoOfertas} onOpenChange={() => setSelectedVehiculoOfertas(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Ofertas para {selectedVehiculoOfertas?.marca} {selectedVehiculoOfertas?.modelo}
            </DialogTitle>
            <DialogDescription>
              {selectedVehiculoOfertas?.anio} • {selectedVehiculoOfertas?.km?.toLocaleString()} km •
              Precio Base: {selectedVehiculoOfertas?.precioBase ? `$${selectedVehiculoOfertas?.precioBase?.toLocaleString('en-US')}` : 'Sin Base'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {selectedVehiculoOfertas && (
              <>
                {ofertas.filter(o => o.vehiculoId === selectedVehiculoOfertas.id).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay ofertas para este vehículo
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ofertas
                      .filter(o => o.vehiculoId === selectedVehiculoOfertas.id)
                      .map((oferta) => {
                        const revendedor = revendedores[oferta.revendedorId];
                        return (
                          <Card key={oferta.id} className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-medium text-lg">
                                  {revendedor?.nombre || 'Revendedor'}
                                </div>
                                {revendedor?.empresa && (
                                  <div className="text-sm text-muted-foreground">
                                    {revendedor.empresa}
                                  </div>
                                )}
                                {revendedor?.whatsapp && (
                                  <div className="text-sm text-muted-foreground">
                                    📱 {revendedor.whatsapp}
                                  </div>
                                )}
                              </div>
                              <Badge variant={badgeVariants[oferta.estado]}>
                                {oferta.estado}
                              </Badge>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-2xl font-bold text-primary">
                                  ${oferta.monto.toLocaleString('en-US')}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(oferta.hora).toLocaleString('es-UY', {
                                    dateStyle: 'short',
                                    timeStyle: 'short'
                                  })}
                                </div>
                              </div>

                              {oferta.estado === 'Pendiente' && selectedVehiculoOfertas.estadoPublicacion !== 'Adjudicada' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleAcceptOffer(oferta.id)}
                                    disabled={!!processingOffer}
                                    data-testid="modal-accept-offer-button"
                                  >
                                    {processingOffer === oferta.id ? (
                                      <><div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" />Procesando...</>
                                    ) : (
                                      <><Check className="h-4 w-4 mr-1" />Aceptar</>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectOffer(oferta.id)}
                                    disabled={!!processingOffer}
                                    data-testid="modal-reject-offer-button"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Rechazar
                                  </Button>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Cambio de Autor */}
      <Dialog open={!!changeAuthorDialog} onOpenChange={(open) => {
        if (!open) {
          setChangeAuthorDialog(null);
          setSelectedNewAuthor('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Autor de la Publicación</DialogTitle>
            <DialogDescription>
              Selecciona un nuevo autor de la lista de usuarios. El autor previo será reemplazado.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedNewAuthor} onValueChange={setSelectedNewAuthor}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un autor..." />
              </SelectTrigger>
              <SelectContent>
                {usuarios.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.nombre} ({u.rol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeAuthorDialog(null)}>Cancelar</Button>
            <Button onClick={handleChangeAuthor} disabled={!selectedNewAuthor}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
            <AlertDialogDescription>
              La publicación será movida a la papelera. Podrás restaurarla más adelante si lo necesitás.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Confirmation Dialog */}
      <AlertDialog open={!!permanentDeleteId} onOpenChange={() => setPermanentDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción es irreversible. La publicación y todas sus ofertas serán eliminadas permanentemente de la base de datos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handlePermanentDelete(permanentDeleteId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Publicaciones;